import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { dateFormat } from '../../utils/formattedDate';
import { getUserCounts, vcListApi, suspendUserApi, restoreUserApi, updateUserDetailApi } from '../../api/usersApi';

import { PageTabs } from '../../components/PageTabs';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import HeaderInfo from '../../components/HeaderInfo';
import Notification from '../../components/Notification';
import AddNewVc from '../../components/VCs/AddNewVc';
import EditUser from '../../components/EditSlider/EditUser';
import ActiveVcs from './ActiveVcs';
import NotCompletedVCs from './NotCompleted';
import SuspendedVCs from './Suspended';
import SingleSuspendPopup from '../../components/SingleSuspendPopup';
import MultipleSuspendPopup from '../../components/MultipleSuspendPopup';

import '../../assets/css/VCs.css'
import '../../assets/css/Popup.css'

const VCs = () => {
    const location = useLocation();
    const userEditRef = useRef();
    const addVcRef = useRef();
    const suspendVcRef = useRef();
    const suspendMultipleVcRef = useRef();
    const debounceRef = useRef(null);

    const [vcsCount, setVcsCount] = useState([]);
    const [activePage, setActivePage] = useState(`Active VCs (${vcsCount[1]})`);
    const [ProfileTabs, setProfileTabs] = useState([]);
    const [menuActive, setMenuActive] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [isEditVCs, setEditVC] = useState(false)
    const [showAddNewVcPopup, setShowAddNewVcPopup] = useState(false);
    const [selectedVC, setSelectVC] = useState({})
    const [isSuspendLoading, setIsSuspendLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true)
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [vcList, setVcList] = useState([]);
    const [totalPage, setTotalPage] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [showVcSuspendPopup, setShowVcSuspendPopup] = useState(false);
    const [showVcMultipleSuspendPopup, setShowVcMultipleSuspendPopup] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [toggleSelect, setToggleSelect] = useState(false);
    const [reason, setReason] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [personalEmail, setPersonalEmail] = useState(selectedVC?.email || '')
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [filterVc, setFilterVc] = useState({
        industry: [],
        startupStage: [],
        teamCount: [],
        plan: [],
        lastActiveDate: '',
        nextBillingDate: '',
        registedDate: '',
        suspendedDate: ''
    })
    const [addVcErrorMessage, setAddVcErrorMessage] = useState({
        name: '',
        email: '',
    })

    const currentStatus = useMemo(() => {
        const path = location?.pathname?.toLowerCase();

        if (path?.includes('/notcompleted')) {
            return 'notCompleted';
        }

        if (path?.includes('/suspended')) {
            return 'suspended';
        }

        return 'active';
    }, [location?.pathname]);

    useEffect(() => {
        const statusLabelMap = {
            active: `Active VCs (${vcsCount[1] || 0})`,
            notCompleted: `Not Completed (${vcsCount[2] || 0})`,
            suspended: `Suspended (${vcsCount[3] || 0})`
        };
        setActivePage(statusLabelMap[currentStatus]);

        setProfileTabs([
            { name: statusLabelMap['active'], href: '/vcs/active' },
            { name: statusLabelMap['notCompleted'], href: '/vcs/notcompleted' },
            { name: statusLabelMap['suspended'], href: '/vcs/suspended' }
        ]);

        setSelectedRows([]);
        setIsAllSelected(false);
    }, [currentStatus, vcsCount]);

    useEffect(() => {
        fetchVcCount();
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setIsLoadingData(true);
        if (debounceRef?.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            fetchVcs();
        }, 1500);

        return () => clearTimeout(debounceRef.current);
    }, [currentPage, filterVc, searchQuery, location?.pathname]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filterVc, searchQuery]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suspendVcRef?.current && !suspendVcRef?.current.contains(event.target)) {
                setShowVcSuspendPopup(false);
            }
            if (suspendMultipleVcRef?.current && !suspendMultipleVcRef?.current.contains(event.target)) {
                setShowVcMultipleSuspendPopup(false);
            }
            if (userEditRef?.current && !userEditRef?.current.contains(event.target)) {
                setEditVC(false);
            }
            if (addVcRef?.current && !addVcRef?.current.contains(event.target)) {
                setShowAddNewVcPopup(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const fetchVcCount = async () => {
        try {
            const res = await getUserCounts({ type: 3 });
            const countsByStatus = {
                1: 0,
                2: 0,
                3: 0,
            };
            res.forEach(item => {
                if (countsByStatus.hasOwnProperty(item?.status)) {
                    countsByStatus[item?.status] = item?.count;
                }
            });
            setVcsCount(countsByStatus);
        } catch (error) {
            console.log(error);
        }
    };

    const handleEditClick = async (row) => {
        try {
            setEditVC(true);
            setSelectVC(row);
        } catch (error) {
            console.error('Failed to fetch user detail on edit:', error);
        }
    };

    const vcApi = async () => {
        const status = currentStatus;

        const data = await vcListApi({
            status,
            page: currentPage || 1,
            search: searchQuery?.toLowerCase() || undefined,
            industry: status !== 'notCompleted' ? filterVc?.industry?.map((item) => item?.toString()) : undefined,
            startupStage: status !== 'notCompleted' ? filterVc?.startupStage?.map((item) => item?.toString()) : undefined,
            teamCount: status !== 'notCompleted' ? filterVc?.teamCount?.map((item) => item?.toString()) : undefined,
            plan: status !== 'notCompleted' ? filterVc?.plan?.map((item) => item?.toString()) : undefined,
            registeredStart: (filterVc?.registedDate?.from !== '' && filterVc?.registedDate?.from !== undefined && filterVc?.registedDate?.from) ? dateFormat(filterVc?.registedDate?.from) : undefined,
            registeredEnd: (filterVc?.registedDate?.to !== '' && filterVc?.registedDate?.to !== undefined && filterVc?.registedDate?.to) ? dateFormat(filterVc?.registedDate?.to) : undefined,
            suspendedStart: status !== 'notCompleted' ? ((filterVc?.suspendedDate?.from !== '' && filterVc?.suspendedDate?.from !== undefined && filterVc?.suspendedDate?.from) ? dateFormat(filterVc?.suspendedDate?.from) : undefined) : undefined,
            suspendedEnd: status !== 'notCompleted' ? ((filterVc?.suspendedDate?.to !== '' && filterVc?.suspendedDate?.to !== undefined && filterVc?.suspendedDate?.to) ? dateFormat(filterVc?.suspendedDate?.to) : undefined) : undefined,
        }, setIsLoadingData);
        return data
    }

    const fetchVcs = async () => {
        setIsLoading(false);
        try {
            setIsLoading(true)
            setIsLoadingData(true)

            const data = await vcApi()
            if (data?.list?.length <= 0) {
                setIsLoadingData(false)
            }
            setVcList(data?.list);
            setTotalPage(data?.totalPage)
            setIsLoading(false)
        } catch (error) {
            console.log(error);
            setIsLoading(false)
            setIsLoadingData(false)
        }
    };

    const suspendUser = async (reason, userId, showVcMultipleSuspendPopup = false) => {
        try {
            await suspendUserApi({
                reason,
                userIds: showVcMultipleSuspendPopup ? userId : [userId],
            });
            return true;
        } catch (error) {
            console.error('API error:', error.response?.data || error.message);
            return false;
        }
    };

    const restoreUser = async (reason, userId, showVcMultipleSuspendPopup = false) => {
        try {
            await restoreUserApi({
                reason,
                userIds: showVcMultipleSuspendPopup ? userId : [userId],
            });
            return true;
        } catch (error) {
            console.error('API error:', error.response?.data || error.message);
            return false;
        }
    }

    const updateUserData = async (userId, email) => {
        try {
            await updateUserDetailApi({
                userId,
                data: { email },
            });
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    const handleUpdate = async ({ userId, email }) => {
        setIsSaveLoading(false)
        setShowNotification(false);
        try {
            setIsSaveLoading(true);
            const success = await updateUserData(userId, email);

            if (success) {
                const data = await vcApi()
                if (data) {
                    setIsLoadingData(false)
                    setVcList(data?.list);
                    setNotificationMessage('Your changes have been successfully saved.')
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 8000);
                    setEditVC(false)
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaveLoading(false);
        }
    };

    const handleUpdateUser = (changedPersonalEmail) => {
        handleUpdate({
            userId: selectedVC?.userId,
            email: changedPersonalEmail,
        });
    };

    const handleUpdateTeamUser = (member) => {
        handleUpdate({
            userId: member?.userId,
            email: member?.email,
        });
    };

    const handleSuspendUser = async (reason, profileData) => {
        setIsSuspendLoading(false)
        setShowNotification(false);
        const notificationText = `${profileData?.name || ""} was suspended from Ivycorn.`;
        try {
            setIsSuspendLoading(true)
            const success = await suspendUser(reason, profileData?.userId);
            if (success) {
                const data = await vcApi()
                await fetchVcCount()
                if (data) {
                    setIsLoadingData(false)
                    setVcList(data?.list);
                    setTotalPage(data?.totalPage)
                    setShowNotification(true);
                    setNotificationMessage(notificationText);
                    setTimeout(() => {
                        setShowNotification(false);
                    }, 8000);
                    setIsSuspendLoading(false)
                    setEditVC(false)
                    if (data?.list?.length === 0) {
                        setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                    }
                }
                return true
            } else {
                setIsSuspendLoading(false)
            }
        } catch (error) {
            console.log(error);
            setIsSuspendLoading(false)
        }
    }

    const handleActionUser = async (reason) => {
        let status = currentStatus;
        setShowNotification(false);
        setErrorMessage('');
        let notificationText = '';
        let success = false;
        if (!reason.trim()) {
            setErrorMessage(
                status !== "suspended"
                    ? "Please enter reason for suspension."
                    : "Please enter reason for restore."
            );
            return;
        }

        setIsSuspendLoading(true)
        if (status === "suspended") {
            if (showVcSuspendPopup) {
                notificationText = `${selectedVC?.name || ""} was restored from Ivycorn.`;
            } else {
                notificationText = `${selectedRows?.length} VCs were restored from Ivycorn.`
            }
            success = await restoreUser(reason, showVcSuspendPopup ? selectedVC?.userId : selectedRows, showVcMultipleSuspendPopup);
        } else {
            if (showVcSuspendPopup) {
                notificationText = `${selectedVC?.name || ""} was suspended from Ivycorn.`;
            } else {
                notificationText = `${selectedRows?.length} VCs were suspended from Ivycorn.`
            }
            success = await suspendUser(reason, showVcSuspendPopup ? selectedVC?.userId : selectedRows, showVcMultipleSuspendPopup);
        }
        if (success) {
            const data = await vcApi()
            await fetchVcCount()
            if (data) {
                setVcList(data?.list);
                setTotalPage(data?.totalPage)
                setShowNotification(true);
                setNotificationMessage(notificationText);
                setToggleSelect(!toggleSelect);
                setTimeout(() => {
                    setShowNotification(false);
                }, 8000);
                setShowVcMultipleSuspendPopup(false)
                setShowVcSuspendPopup(false)
                setReason('')
                if (data?.list?.length === 0) {
                    setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                }
            }
            setIsSuspendLoading(false)
        } else {
            setIsSuspendLoading(false)
        }
    };

    const LazyImage = ({ src, alt }) => {
        const [isVisible, setIsVisible] = useState(false);
        const imgRef = useRef();

        useEffect(() => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.disconnect();
                    }
                },
                { threshold: 0.1 }
            );

            if (imgRef.current) {
                observer.observe(imgRef.current);
            }

            return () => {
                if (imgRef.current) {
                    observer.unobserve(imgRef.current);
                }
            };
        }, []);

        return (
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                loading="lazy"
            />
        );
    };

    const vcUserList = useMemo(() => {
        return vcList
    }, [vcList])

    const vcProps = {
        setShowAddNewVcPopup,
        setSelectVC,
        isLoading,
        isLoadingData,
        vcList: vcUserList,
        totalPage,
        setEditVC,
        setIsLoadingData,
        currentPage,
        setSearchQuery,
        searchQuery,
        setFilterVc,
        filterVc,
        setShowVcSuspendPopup,
        setShowVcMultipleSuspendPopup,
        selectedRows,
        setSelectedRows,
        isAllSelected,
        setIsAllSelected,
        toggleSelect,
        setErrorMessage,
        setCurrentPage,
        setReason,
        LazyImage,
        setAddVcErrorMessage
    };

    const getVcComponent = (Component) => <Component {...vcProps} />;

    return (
        <div className='main-wrapper'>
            <Header activePage="vcs" menuActive={menuActive} setMenuActive={setMenuActive} />
            <div className="main-content">
                <div className={`content-wrapper`}>
                    <HeaderInfo title='VCs' description={
                        activePage.startsWith('Active')
                            ? `You have ${vcsCount[1] || 0} active VCs.`
                            : activePage.startsWith('Not Completed')
                                ? `You have ${vcsCount[2] || 0} not completed VCs.`
                                : activePage.startsWith('Suspended')
                                    ? `You have ${vcsCount[3] || 0} suspended VCs.`
                                    : ''
                    } />
                    <PageTabs tabs={ProfileTabs} activePage={activePage} setActivePage={(tabName) => {
                        setActivePage(tabName);
                        setFilterVc({
                            industry: [],
                            startupStage: [],
                            teamCount: [],
                            plan: [],
                            lastActiveDate: '',
                            nextBillingDate: '',
                            registedDate: '',
                            suspendedDate: ''
                        });
                        setSearchQuery('')
                    }} />
                    <div className="vcsContent">
                        <Routes basename="/vcs">
                            <Route path="/" element={getVcComponent(ActiveVcs)} />
                            <Route path="/active" element={getVcComponent(ActiveVcs)} />
                            <Route path="/notcompleted" element={getVcComponent(NotCompletedVCs)} />
                            <Route path="/suspended" element={getVcComponent(SuspendedVCs)} />
                        </Routes>
                    </div>
                </div>
                <Footer menuActive={menuActive} />
            </div>

            {
                showNotification &&
                <Notification showNotification={showNotification} notificationMessage={notificationMessage} setShowNotification={setShowNotification} />
            }
            {
                showAddNewVcPopup &&
                <AddNewVc
                    ref={addVcRef}
                    isOpen={showAddNewVcPopup}
                    onClose={() => setShowAddNewVcPopup(false)}
                    setShowAddNewVcPopup={setShowAddNewVcPopup}
                    setShowNotification={setShowNotification}
                    setNotificationMessage={setNotificationMessage}
                    addVcErrorMessage={addVcErrorMessage}
                    setAddVcErrorMessage={setAddVcErrorMessage}
                />
            }
            {
                showVcSuspendPopup &&
                <SingleSuspendPopup
                    isOpen={showVcSuspendPopup}
                    onClose={() => { setShowVcSuspendPopup(false) }}
                    title={`Are you sure you want to ${activePage == `Suspended (${vcsCount[3] || 0})` ? 'restore' : 'suspend'} ${selectedVC?.name}?`}
                    description={`${activePage === `Suspended (${vcsCount[3] || 0})` ? "Restoring this VC will reinstate their access to Ivycorn, allowing them to continue using the service." : "Suspending this VC means they will be permanently suspended from using Ivycorn."}`}
                    ref={suspendVcRef}
                    status={activePage === `Suspended (${vcsCount[3] || 0})` && 'suspended'}
                    handleActionUser={handleActionUser}
                    reason={reason}
                    setReason={setReason}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    isSuspendLoading={isSuspendLoading}
                />
            }
            {
                showVcMultipleSuspendPopup &&
                <MultipleSuspendPopup
                    ref={suspendMultipleVcRef}
                    isOpen={showVcMultipleSuspendPopup}
                    onClose={() => { setShowVcMultipleSuspendPopup(false) }}
                    title={`Are you sure you want to ${activePage === `Suspended (${vcsCount[3] || 0})` ? 'restore' : 'suspend'} these ${selectedRows?.length} VCs?`}
                    description={`${activePage === `Suspended (${vcsCount[3] || 0})` ? "Restoring these VCs will reinstate their access to Ivycorn, allowing them to continue using the service." : "Suspending these VCs means they will be permanently suspended from using Ivycorn."}`}
                    handleActionUser={handleActionUser}
                    reason={reason}
                    setReason={setReason}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    status={activePage === `Suspended (${vcsCount[3] || 0})` && 'suspended'}
                    isSuspendLoading={isSuspendLoading}
                />
            }
            <EditUser
                setEdit={setEditVC}
                ref={userEditRef}
                selectedUser={selectedVC}
                isEditUser={isEditVCs}
                type='vc'
                handleActionUser={handleSuspendUser}
                isSuspendLoading={isSuspendLoading}
                setSelectUser={setSelectVC}
                setPersonalEmail={setPersonalEmail}
                personalEmail={personalEmail}
                isSaveLoading={isSaveLoading}
                setIsSaveLoading={setIsSaveLoading}
                handleUpdateUser={handleUpdateUser}
                handleUpdateTeamUser={handleUpdateTeamUser}
                reason={reason}
                setReason={setReason}
            />
        </div>
    )
}

export default VCs
